import React from 'react';
import { useTranslation } from 'react-i18next';
import { PublicProfile } from '../../types/profile';

interface BotProfileViewProps {
    profile: PublicProfile;
    onStartChat: () => void;
    chatLoading?: boolean;
}

const BotProfileView: React.FC<BotProfileViewProps> = ({ profile, onStartChat, chatLoading = false }) => {
    const { t } = useTranslation('profile');
    const displayName = profile.fullName || profile.username;
    const initial = displayName.charAt(0).toUpperCase();
    const persona = profile.botPersona;

    const personaTypeLabel = persona
        ? t(`bot.type.${persona.personaType}`, { defaultValue: '' })
        : '';

    return (
        <div className="bot-profile-view">
            {/* Hero — quiet card, no gradient */}
            <section className="bot-hero">
                <div className="bot-hero__avatar">
                    {profile.avatarUrl ? (
                        <img src={profile.avatarUrl} alt={displayName} />
                    ) : (
                        <div className="bot-hero__avatar-initial">{initial}</div>
                    )}
                </div>

                <div className="bot-hero__body">
                    <div className="bot-hero__name-row">
                        <h1 className="bot-hero__name">{displayName}</h1>
                        <span className="bot-hero__badge">{t('bot.badge')}</span>
                    </div>
                    <p className="bot-hero__handle">@{profile.username}</p>

                    {persona?.tagline && (
                        <p className="bot-hero__tagline">{persona.tagline}</p>
                    )}
                    {!persona?.tagline && profile.bio && (
                        <p className="bot-hero__tagline">{profile.bio}</p>
                    )}

                    <button
                        className="bot-hero__cta"
                        onClick={onStartChat}
                        disabled={chatLoading}
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        {t('bot.startChat')}
                    </button>
                </div>
            </section>

            {/* Persona details */}
            {persona && (
                <section className="bot-about">
                    <h2 className="bot-about__heading">{t('bot.aboutHeading')}</h2>

                    {personaTypeLabel && (
                        <div className="bot-about__row">
                            <span className="bot-about__label">{t('bot.personalityHeading')}</span>
                            <span className="bot-about__value">{personaTypeLabel}</span>
                        </div>
                    )}

                    {persona.topics && persona.topics.length > 0 && (
                        <div className="bot-about__topics">
                            <div className="bot-about__label">{t('bot.topicsHeading')}</div>
                            <p className="bot-about__hint">{t('bot.topicsHint')}</p>
                            <div className="bot-about__topic-chips">
                                {persona.topics.map((topic) => (
                                    <span key={topic} className="bot-about__chip">
                                        {topic}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* How to use */}
            <section className="bot-howto">
                <h2 className="bot-howto__heading">{t('bot.howToHeading')}</h2>
                <p className="bot-howto__body">{t('bot.howToBody')}</p>
                <button
                    className="bot-howto__cta"
                    onClick={onStartChat}
                    disabled={chatLoading}
                >
                    {t('bot.startChat')} →
                </button>
            </section>
        </div>
    );
};

export default BotProfileView;
